/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { useStateRef } from '@flowus/common/hooks/react-utils';
import { fastEqual } from '@flowus/common/utils/tools';
import { debounce, throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from 'src/redux/types';
import { v4, validate } from 'uuid';
import { useFuSelector } from '../store-context/use-fine-context-selector';
import { useIsEnableRedux } from './common';
import { observableStoreOp } from './map';
import {
  getSelectedBlocksKey,
  getSelectedBlocksKeyByBlock,
  getSelectedBlocksKeyBySync,
  getSelectedBlocksKeyByView,
  getSelectedCellKey,
  isSelectPrefixId,
  isSimpleTableKey,
  prefixSimpleTable,
} from './patch';
import type { ObservableStoreProps, ObservableStoreSelector } from './types';
import { DEBOUNCE_WAIT_NUM, LEADING, THROTTLE_WAIT_NUM } from './types';

const useCreateProxyStore = (opt?: { log?: boolean }) => {
  const reduxStore = useStore();

  return useCallback(
    (
      storeKey: 'blocks' | 'discussions' | 'comments' | 'simpleTable' | 'collectionViews',
      callback?: (key: string) => void
    ) => {
      return new Proxy(
        {},
        {
          get(_, p: string, receiver) {
            if (opt?.log) {
              // 输出log
            }
            callback?.(p);
            return Reflect.get((reduxStore.getState() as RootState)[storeKey], p, receiver);
          },
        }
      );
    },
    []
  );
};

// #region useObservableStore

/**
 * 用于订阅store中的数据，当数据发生变化时，会自动更新组件
 * @author kcsx
 * @param selector 获取想要的数据，参数中的`blocks`是一个proxy对象，会记录下来使用到的blockId，当block发生变化时会接收通知
 * @param dep 依赖项，如果传入的是blockId，会自动订阅block
 * @param opt 额外的选项
 */
export function useObservableStore<T>(
  selector: ObservableStoreSelector<T>,
  dep: ObservableStoreProps['dep'],
  opt?: ObservableStoreProps['opt']
): T {
  const isEnableRedux = useIsEnableRedux();
  if (!opt?.useRx && (isEnableRedux || opt?.useRedux)) {
    return useFuSelector((state) => {
      return selector({
        ...state,
        simpleTable: state.simpleTable,
        selectedBlocks: state.ui.selectedBlocks,
        selectedCells: state.ui.selectedCells,
      });
    });
  }

  // #region 声明
  const {
    obsSelectBlocks = [],
    obsSelectCell = [],
    wait,
    waitMode = 'debounce',
    obsSimpleTable,
    log,
  } = opt || {};
  const reduxStore = useStore();
  const createProxyStore = useCreateProxyStore({ log });
  const [ids, setIds, idsRef] = useStateRef(new Set<string>());
  const [deps, setDeps, depsRef] = useStateRef({ dep, opt });
  const proxy = useRef(
    (() => {
      const debounceSetIds = debounce(setIds, DEBOUNCE_WAIT_NUM, { trailing: true });
      const callbackByObj = (id: string) => {
        if (!idsRef.current.has(id)) {
          idsRef.current.add(id);
          debounceSetIds(new Set(idsRef.current));
        }
      };

      return {
        blocks: createProxyStore('blocks', callbackByObj),
        discussions: createProxyStore('discussions', callbackByObj),
        comments: createProxyStore('comments', callbackByObj),
        collectionViews: createProxyStore('collectionViews', callbackByObj),
      };
    })()
  );
  const runSelector = useCallback(() => {
    // const cacheKey = `${selector}`;
    // // @ts-ignore debug
    // const debugMap: any = window.debugMap ? new Map(window.debugMap) : new Map<string, number>();
    // if (debugMap.has(cacheKey)) {
    //   debugMap.set(cacheKey, debugMap.get(cacheKey)! + 1);
    // } else {
    //   debugMap.set(cacheKey, 1);
    // }
    // // @ts-ignore debug
    // window.debugMap = [...debugMap].sort((a, b) => b[1] - a[1]);
    const store = reduxStore.getState() as RootState;
    return selector({
      ...proxy.current,
      selectedBlocks: store.ui.selectedBlocks,
      selectedCells: store.ui.selectedCells,
      simpleTable: store.simpleTable,
    });
  }, [ids, deps, dep, opt]);

  const [uniqueKey, setUniqueKey] = useStateRef('');
  const lastState = useRef({
    data: runSelector(),
    uniqueKey,
  });
  // #endregion

  // 更新deps
  useEffect(() => {
    const newDep = { dep, opt };
    if (fastEqual(depsRef.current, newDep)) {
      return;
    }
    setDeps(newDep);
  }, [dep, opt]);

  // 更新订阅
  useEffect(() => {
    const subMap = new Map();

    const compIds = new Set(ids);

    // #region 额外订阅 id
    // 订阅deps中的 blockId
    deps.dep.forEach((id) => {
      if (compIds.has(id) || !validate(id)) return;
      compIds.add(id);
    });

    // 订阅 simpleTable
    if (obsSimpleTable) {
      compIds.add(prefixSimpleTable);
    }

    // 选中block
    obsSelectBlocks.forEach((item) => {
      if (item.syncId && item.blockId && item.viewId) {
        const key = getSelectedBlocksKey(item);
        compIds.add(key);
      } else {
        // 只监听blockId
        if (item.blockId) {
          const key = getSelectedBlocksKeyByBlock(item.blockId);
          compIds.add(key);
        }

        // 监听viewId
        if (item.viewId) {
          const key = getSelectedBlocksKeyByView(item.viewId);
          compIds.add(key);
        }

        // 监听syncId
        if (item.syncId) {
          const key = getSelectedBlocksKeyBySync(item.syncId);
          compIds.add(key);
        }
      }
    });

    // 选中cell
    obsSelectCell.forEach((item) => {
      if (item.recordId && item.viewId && item.propertyId) {
        const key = getSelectedCellKey(item);
        compIds.add(key);
      } else {
        if (item.recordId) {
          const key = getSelectedCellKey({ recordId: item.recordId });
          compIds.add(key);
        }

        if (item.viewId) {
          const key = getSelectedCellKey({ viewId: item.viewId });
          compIds.add(key);
        }

        if (item.propertyId) {
          const key = getSelectedCellKey({ propertyId: item.propertyId });
          compIds.add(key);
        }
      }
    });
    // #endregion

    // #region 开始订阅
    const patchState = () => {
      const newState = runSelector();
      if (fastEqual(lastState.current.data, newState)) {
        return;
      }

      lastState.current = {
        data: newState,
        uniqueKey: v4(),
      };

      setUniqueKey(lastState.current.uniqueKey);
    };

    const patchStateDebounce = debounce(patchState, wait ?? DEBOUNCE_WAIT_NUM, {
      leading: LEADING,
      trailing: true,
    });

    const patchStateThrottle = throttle(patchState, wait ?? THROTTLE_WAIT_NUM, {
      leading: LEADING,
      trailing: true,
    });

    compIds.forEach((id) => {
      if (subMap.has(id)) {
        return;
      }
      const isThrottle = isSelectPrefixId(id) || isSimpleTableKey(id) || waitMode === 'throttle';
      const obs$ = observableStoreOp.get(id);
      const cb = (() => {
        if (wait === 0 || waitMode === 'none') {
          return patchState;
        }
        if (isThrottle) {
          return patchStateThrottle;
        }
        return patchStateDebounce;
      })();
      const sub = obs$.subscribe(cb);
      subMap.set(id, sub);
    });
    // #endregion

    if (lastState.current.uniqueKey) {
      patchState();
    }

    return () => {
      patchStateThrottle.cancel();
      patchStateDebounce.cancel();
      subMap.forEach((sub) => {
        sub.unsubscribe();
      });
      subMap.clear();
    };
  }, [deps, ids, wait]);

  return useMemo(() => {
    // 检查依赖是否发生变化
    const checkEqual = fastEqual(depsRef.current, { dep, opt });

    // 检查更新key是否发生变化
    const checkKey = lastState.current.uniqueKey === uniqueKey;

    // 有个情况是整个 hook 会经常刷新，导致 ref 无限重建。特加逻辑让 uniqueKey 有值时才走缓存
    if (checkEqual && checkKey && lastState.current.uniqueKey) {
      return lastState.current.data;
    }

    const newState = runSelector();
    lastState.current.data = newState;
    return lastState.current.data;
  }, [uniqueKey, runSelector]);
}
// #endregion
